import { ReactNode, createContext, useEffect, useRef, useState } from 'react'
import { CopilotUploadPreset } from 'shared/types/copilot'
import { AlertData, useAlert } from 'ui'
import Alert from '../alert'
import { CopilotPresetAlert } from '../types'
import { descriptionFromPresetAlert, titleFromPresetAlert } from '../utils/preset-alert'

const DEFAULT_ALERT_DATA: AlertData = {
    title: '',
}

export type CopilotAlertContextProps = {
    showAlert: (alert: CopilotPresetAlert, preset?: CopilotUploadPreset) => Promise<string>
}

export const CopilotAlertContext = createContext({} as CopilotAlertContextProps)

const CopilotAlertContextManager = ({ children }: { children: ReactNode }) => {
    const promiseResolveRef = useRef<(value: unknown) => unknown>(() => {})
    const [alert, setAlert] = useState<CopilotPresetAlert | null>(null)
    const [alertData, setAlertData] = useState<AlertData>(DEFAULT_ALERT_DATA)
    const [preset, setPreset] = useState<CopilotUploadPreset | undefined>()
    const { isOpen, closeAlert, openAlert } = useAlert(alertData)

    async function showAlert(alert: CopilotPresetAlert, preset?: CopilotUploadPreset) {
        setAlert(alert)
        setAlertData({
            closeable: true,
            description: descriptionFromPresetAlert(alert),
            title: titleFromPresetAlert(alert),
        })
        setPreset(preset)
        openAlert()

        return new Promise((resolve) => {
            promiseResolveRef.current = resolve
        }) as Promise<string>
    }

    function onContinue(value: string) {
        promiseResolveRef.current(value)
        closeAlert()
    }

    useEffect(() => {
        if (!isOpen) {
            promiseResolveRef.current = () => {}
            setAlert(null)
        }
    }, [isOpen])

    return (
        <CopilotAlertContext.Provider value={{ showAlert }}>
            {children}
            <Alert
                alert={alert}
                isOpen={isOpen}
                preset={preset}
                onCancel={() => closeAlert()}
                onContinue={onContinue}
            />
        </CopilotAlertContext.Provider>
    )
}

export default CopilotAlertContextManager
